import type { OrderedSourceSnapshot } from './ordered-source';
import { orderHolds } from './site-games';

export type UnpublishedListsInput = {
  /** The game lists on the page. */
  listIds: string[];
  /** Orders the owner changed in this session, by list. */
  drafts: Record<string, string[]>;
  /** Each list's order as the site shows it, for the lists the site sent. */
  siteOrders: Record<string, string[]>;
  /** What the service holds for each list it answered for: its saved draft and its live order. */
  snapshots: Record<string, OrderedSourceSnapshot>;
  /** The open list, and whether its Publish button is on (when that is known; else the rule below applies). */
  activeId?: string;
  activeDirty?: boolean;
};

/**
 * The lists on the page whose game order has unpublished changes: those Publish would change on the site.
 * The open list goes by its own Publish button. Any other list: the order being worked on (a change made in
 * this session, else the draft the service saved; an empty saved draft is none) differs from what the site
 * shows (the order it sent, else the service's live order). A worked-on list with neither counts as changed.
 */
export const unpublishedLists = ({
  listIds,
  drafts,
  siteOrders,
  snapshots,
  activeId,
  activeDirty,
}: UnpublishedListsInput): Set<string> =>
  new Set(
    listIds.filter((listId) => {
      if (listId === activeId && activeDirty !== undefined) return activeDirty;
      const saved = snapshots[listId];
      // An empty order is no order, wherever it comes from. publishOrderFor refuses to send one, so a
      // list counted on the strength of one would be reported as failed on every retry, forever.
      const working = (drafts[listId]?.length ? drafts[listId] : null) ?? (saved?.draft?.length ? saved.draft : null);
      if (!working) return false;
      const live = siteOrders[listId] ?? (saved?.live.length ? saved.live : null);
      return live ? !orderHolds(working, live) : true;
    }),
  );


export type PublishOrderInput = {
  /** The list about to be published. */
  listId: string;
  /** The open list, whose working order the tab holds directly. */
  activeId?: string;
  /** The open list's working order, and whether it actually differs from what the site shows. */
  activeOrder?: readonly string[];
  activeDirty?: boolean;
  /** Orders the owner changed in this session, by list. */
  drafts: Record<string, string[]>;
  /** What the service holds for each list it answered for. */
  snapshots: Record<string, OrderedSourceSnapshot>;
};

/**
 * The order "Publish all" should send for one list, or `null` when there is nothing to send for it.
 *
 * The open list goes by its working order, but ONLY when that is a real change: its working order falls
 * back to the site's current order, and publishing that would overwrite the owner's saved draft with the
 * order the site already renders: a no-op publish that silently destroys their reorder. Every other list
 * goes by the order being worked on: this session's change, else the draft the service saved.
 *
 * `null` is a refusal, not a skip. A list the bar counted but that resolves to nothing must be reported as
 * failed, or the run ends on "Published 0 lists. Your site now shows them." with every dot still in place.
 */
export const publishOrderFor = ({
  listId,
  activeId,
  activeOrder,
  activeDirty,
  drafts,
  snapshots,
}: PublishOrderInput): string[] | null => {
  if (listId === activeId && activeDirty && activeOrder && activeOrder.length > 0) return [...activeOrder];
  // An empty local draft is no draft, exactly as unpublishedLists reads it, so the saved one still stands.
  // Short-circuiting on it here instead would refuse a list that the bar had counted on the saved draft,
  // and every retry would report it failed with nothing the owner could do.
  const local = drafts[listId]?.length ? drafts[listId] : null;
  const saved = local ?? snapshots[listId]?.draft ?? null;
  return saved && saved.length > 0 ? [...saved] : null;
};
